import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  McpServer,
  McpServerCreate,
  McpServerPermission,
  McpServerPermissionUpsert,
  McpServerUpdate,
  McpToolsResponse,
} from "@/api/types";

const KEY = ["mcp-servers"] as const;
const KEY_PERMS = (mcpId: string) => ["mcp-permissions", mcpId] as const;
const KEY_TOOLS = (mcpId: string) => ["mcp-tools", mcpId] as const;

export function useMcps(filters: { include_inactive?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () =>
      api.GET<McpServer[]>("/mcp-servers", {
        params: { include_inactive: Boolean(filters.include_inactive) },
      }),
    // Refresh a cada 60s para refletir o beat de health-check.
    refetchInterval: 60_000,
  });
}

export function useMcp(mcpId: string) {
  return useQuery({
    queryKey: [...KEY, mcpId],
    queryFn: () => api.GET<McpServer>(`/mcp-servers/${mcpId}`),
    enabled: Boolean(mcpId),
  });
}

export function useCreateMcp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: McpServerCreate) =>
      api.POST<McpServer>("/mcp-servers", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateMcp(mcpId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: McpServerUpdate) =>
      api.PATCH<McpServer>(`/mcp-servers/${mcpId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_TOOLS(mcpId) });
    },
  });
}

export function useDeactivateMcp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mcpId: string) =>
      api.DELETE<void>(`/mcp-servers/${mcpId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useMcpHealthCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mcpId: string) =>
      api.POST<McpServer>(`/mcp-servers/${mcpId}/health-check`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useMcpTools(mcpId: string) {
  return useQuery({
    queryKey: KEY_TOOLS(mcpId),
    queryFn: () => api.GET<McpToolsResponse>(`/mcp-servers/${mcpId}/tools`),
    enabled: Boolean(mcpId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

// --- Permissions ----------------------------------------------------------

export function useMcpPermissions(mcpId: string) {
  return useQuery({
    queryKey: KEY_PERMS(mcpId),
    queryFn: () =>
      api.GET<McpServerPermission[]>(`/mcp-servers/${mcpId}/permissions`),
    enabled: Boolean(mcpId),
  });
}

export function useGrantMcpPermission(mcpId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: McpServerPermissionUpsert) =>
      api.POST<McpServerPermission>(
        `/mcp-servers/${mcpId}/permissions`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PERMS(mcpId) });
    },
  });
}

export function useRevokeMcpPermission(mcpId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) =>
      api.DELETE<void>(`/mcp-servers/${mcpId}/permissions/${teamId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PERMS(mcpId) });
    },
  });
}
